import React, { useContext, useMemo, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { TradingContext } from '../../contexts/TradingContext';
import { AppStateContext } from '../../contexts/AppStateContext';
import { StreamsContext } from '../../contexts/StreamsContext';
import { colors } from '../../theme/colors';
import useCopyAppState from '../../hooks/useCopyAppState';
import { useSubmitBugReport } from '../../hooks/useBackend';
import useCandleStickChartData from '../../providers/CandleStickChartProvider';
import { pickQuoteFields } from '../../utils/quoteFields';

const Section = ({ title, children, defaultOpen = false }) => (
  <details open={defaultOpen} style={{ borderBottom: `1px solid ${colors.borderLight}`, padding: '6px 0' }}>
    <summary style={{ cursor: 'pointer', color: colors.primary, fontWeight: 600 }}>{title}</summary>
    <div style={{ marginTop: 6 }}>{children}</div>
  </details>
);

const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);
const isArray = Array.isArray;

const sanitizeValue = (v) => {
  if (typeof v === 'function') return '[Function]';
  if (v instanceof Date) return v.toISOString();
  return v;
};

const removeFunctionsDeep = (value) => {
  if (typeof value === 'function') return undefined;
  if (Array.isArray(value)) {
    return value
      .map((item) => removeFunctionsDeep(item))
      .filter((item) => item !== undefined);
  }
  if (value && typeof value === 'object') {
    const out = {};
    Object.entries(value).forEach(([k, v]) => {
      const next = removeFunctionsDeep(v);
      if (next !== undefined) out[k] = next;
    });
    return out;
  }
  return value;
};

const CollapsibleJsonNode = ({ name, value, level = 0, defaultCollapsed = false }) => {
  const [expanded, setExpanded] = useState(!defaultCollapsed);
  const paddingLeft = level * 12;

  if (isArray(value)) {
    const length = value.length;
    return (
      <div style={{ paddingLeft }}>
        <span
          style={{ cursor: 'pointer', userSelect: 'none', color: colors.primary, marginRight: 4 }}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? '▾' : '▸'}
        </span>
        <strong style={{ color: colors.text }}>{name ? `${name}: ` : ''}</strong>
        <span style={{ color: colors.textLight }}>Array({length})</span>
        {expanded && (
          <div>
            {value.map((item, idx) => (
              <CollapsibleJsonNode
                key={idx}
                name={String(idx)}
                value={sanitizeValue(item)}
                level={level + 1}
                defaultCollapsed={isArray(item) ? item.length > 5 : isObject(item) && Object.keys(item || {}).length > 5}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  if (isObject(value)) {
    const keys = Object.keys(value || {});
    return (
      <div style={{ paddingLeft }}>
        <span
          style={{ cursor: 'pointer', userSelect: 'none', color: colors.primary, marginRight: 4 }}
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? '▾' : '▸'}
        </span>
        <strong style={{ color: colors.text }}>{name ? `${name}: ` : ''}</strong>
        <span style={{ color: colors.textLight }}>{`{${keys.slice(0, 3).join(', ')}${keys.length > 3 ? ', …' : ''}}`}</span>
        {expanded && (
          <div>
            {keys.map((k) => (
              <CollapsibleJsonNode
                key={k}
                name={k}
                value={sanitizeValue(value[k])}
                level={level + 1}
                defaultCollapsed={isArray(value[k]) ? (value[k].length > 5) : isObject(value[k]) && Object.keys(value[k] || {}).length > 5}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Primitive
  return (
    <div style={{ paddingLeft }}>
      {name && <strong style={{ color: colors.text }}>{name}: </strong>}
      <span style={{ color: colors.textLight }}>{JSON.stringify(value)}</span>
    </div>
  );
};

const JsonView = ({ value }) => {
  return (
    <div style={{
      background: 'transparent',
      color: colors.text,
      fontSize: 12,
      lineHeight: 1.4,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      margin: 0
    }}>
      <CollapsibleJsonNode name={null} value={sanitizeValue(value)} level={0} defaultCollapsed={false} />
    </div>
  );
};

const StateInspector = ({ onClose }) => {
  const auth = useContext(AuthContext);
  const trading = useContext(TradingContext);
  const app = useContext(AppStateContext);
  const streams = useContext(StreamsContext);
  const { copyState } = useCopyAppState();
  const { submitBugReport } = useSubmitBugReport();

  // Bug report form state
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

  // Avoid dumping massive maps directly
  const streamsPreview = useMemo(() => {
    // Create barsByKey summary showing each key and number of bars
    const barsByKeySummary = {};
    if (streams.barsByKey) {
      Object.keys(streams.barsByKey).forEach(key => {
        const bars = streams.barsByKey[key];
        barsByKeySummary[key] = Array.isArray(bars) ? bars.length : 0;
      });
    }
    
    return {
      barStreamHealthy: streams.barStreamHealthy,
      quoteStreamHealthy: streams.quoteStreamHealthy,
      positionsStreamHealthy: streams.positionsStreamHealthy,
      quotesBySymbolCount: Object.keys(streams.quotesBySymbol || {}).length,
      barsByKey: barsByKeySummary, // Show each key with bar count
      barsByKeyCount: Object.keys(barsByKeySummary).length, // Total number of keys
      quotesBySymbolKeys: Object.keys(streams.quotesBySymbol || {}).slice(0, 25),
      marketAggregatesCounts: {
        bids: (streams.marketAggregates?.bids || []).length,
        asks: (streams.marketAggregates?.asks || []).length,
      }
    };
  }, [streams]);

  // All quotes data
  const allQuotes = useMemo(() => {
    const symbols = Object.keys(streams.quotesBySymbol || {});
    const quotes = {};
    symbols.forEach((sym) => {
      const picked = pickQuoteFields(streams.quotesBySymbol[sym], { symbol: sym });
      if (picked) quotes[sym] = picked;
    });
    return quotes;
  }, [streams.quotesBySymbol]);

  // Get last 5 bars from active chart
  const activeChartBars = useMemo(() => {
    if (!app?.activeChartId || !app?.charts || !streams?.barsByKey) {
      return null;
    }

    // Find the active chart
    const activeChart = app.charts.find(c => c.id === app.activeChartId);
    if (!activeChart || !activeChart.ticker || !activeChart.interval || !activeChart.unit) {
      return null;
    }

    // Build the bar key (same format as useBarStreamData)
    const sessionTemplate = app.sessionTemplate || 'Default';
    const barsKey = [activeChart.ticker, activeChart.interval, activeChart.unit, sessionTemplate].join('|');

    // Get bars for this key
    const bars = streams.barsByKey[barsKey];
    if (!Array.isArray(bars) || bars.length === 0) {
      return null;
    }

    // Return the last 5 bars (most recent first)
    const lastFive = bars.slice(-5).reverse(); // Reverse to show newest first
    
    return {
      chartInfo: {
        ticker: activeChart.ticker,
        interval: activeChart.interval,
        unit: activeChart.unit,
        sessionTemplate: sessionTemplate,
        barsKey: barsKey,
        totalBars: bars.length
      },
      bars: lastFive
    };
  }, [app?.activeChartId, app?.charts, app?.sessionTemplate, streams?.barsByKey]);

  // Get active chart info for chartData hook
  const activeChartInfo = useMemo(() => {
    if (!app?.activeChartId || !app?.charts) {
      return null;
    }

    const activeChart = app.charts.find(c => c.id === app.activeChartId);
    if (!activeChart || !activeChart.ticker || !activeChart.interval || !activeChart.unit) {
      return null;
    }

    return {
      ticker: activeChart.ticker,
      interval: activeChart.interval,
      unit: activeChart.unit,
      sessionTemplate: app.sessionTemplate || 'Default',
    };
  }, [app?.activeChartId, app?.charts, app?.sessionTemplate]);

  // Get chartData using the same transformation as the chart (hook must always be called)
  const { chartData } = useCandleStickChartData(
    activeChartInfo?.ticker || null,
    activeChartInfo?.interval || 5,
    activeChartInfo?.unit || 'Minute',
    200,
    activeChartInfo?.sessionTemplate || 'Default'
  );

  // Get last 5 chartData items (most recent first)
  const lastFiveChartData = useMemo(() => {
    if (!Array.isArray(chartData) || chartData.length === 0) {
      return null;
    }
    return chartData.slice(-5).reverse(); // Reverse to show newest first
  }, [chartData]);

  // Filter orders to only include today and yesterday
  const filterRecentOrders = (orders) => {
    if (!orders || !Array.isArray(orders)) return [];
    
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(0, 0, 0, 0);
    
    return orders.filter(order => {
      const orderDate = new Date(order.OpenedDateTime || order.ClosedDateTime || order.TimeStamp || 0);
      return orderDate >= yesterday;
    });
  };

  // Gather full state snapshot
  const stateSnapshot = useMemo(() => ({
    auth: { user: auth.user },
    trading: {
      selectedTicker: trading.selectedTicker,
      selectedAccount: trading.selectedAccount,
      isPaperTrading: trading.isPaperTrading,
      balances: trading.balances || [],
      positions: trading.positions || [],
      orders: filterRecentOrders(trading.orders),
      currentOrders: filterRecentOrders(streams.currentOrders),
      ordersMetadata: {
        totalOrders: (trading.orders || []).length,
        totalCurrentOrders: (streams.currentOrders || []).length,
        recentOrdersCount: filterRecentOrders(trading.orders).length,
        recentCurrentOrdersCount: filterRecentOrders(streams.currentOrders).length
      }
    },
    app: removeFunctionsDeep(app),
    streams: streamsPreview,
    marketAggregates: streams.marketAggregates,
    quotes: {
      selectedTicker: trading.selectedTicker,
      selectedQuote: pickQuoteFields(
        streams.quotesBySymbol?.[trading.selectedTicker],
        { symbol: trading.selectedTicker }
      ),
      quotesCount: Object.keys(streams.quotesBySymbol || {}).length,
      allQuotes: allQuotes
    }
  }), [auth, trading, app, streams, streamsPreview, allQuotes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!subject.trim() || !description.trim()) {
      alert('Please fill in both subject and description');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await submitBugReport({
        email: auth.user?.email || '',
        subject: subject.trim(),
        description: description.trim(),
        stateSnapshot,
        userId: auth.user?.id,
        timestamp: new Date().toISOString()
      });

      if (result && !result.error) {
        console.log('Bug report submitted:', result);
        setSubmitStatus('success');
        setSubject('');
        setDescription('');
        
        // Auto-close after 2 seconds on success
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        throw new Error(result?.error || 'Failed to submit bug report');
      }
    } catch (error) {
      console.error('Error submitting bug report:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      bottom: 16,
      right: 16,
      width: 'min(520px, 92vw)',
      maxHeight: '70vh',
      overflow: 'auto',
      background: colors.bgSecondary,
      border: `1px solid ${colors.borderLight}`,
      borderRadius: 8,
      boxShadow: '0 6px 24px rgba(0,0,0,0.4)',
      padding: 12,
      zIndex: 4000
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ color: colors.text, fontWeight: 700 }}>🐛 Submit Bug Report</div>
        <button
          onClick={onClose}
          className="btn btn-sm btn-link"
          style={{ color: colors.text, textDecoration: 'none', padding: 0 }}
          title="Close"
        >
          ✕
        </button>
      </div>

      {/* Bug Report Form */}
      <form onSubmit={handleSubmit} style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', color: colors.text, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief description of the issue"
            required
            disabled={isSubmitting}
            style={{
              width: '100%',
              padding: '8px',
              background: colors.bgPrimary,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: 4,
              color: colors.text,
              fontSize: 13,
              outline: 'none'
            }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', color: colors.text, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Detailed description of the issue, steps to reproduce, expected vs actual behavior..."
            required
            disabled={isSubmitting}
            rows={4}
            style={{
              width: '100%',
              padding: '8px',
              background: colors.bgPrimary,
              border: `1px solid ${colors.borderLight}`,
              borderRadius: 4,
              color: colors.text,
              fontSize: 13,
              outline: 'none',
              resize: 'vertical'
            }}
          />
        </div>

        {submitStatus === 'success' && (
          <div style={{
            padding: '8px 12px',
            background: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            borderRadius: 4,
            color: '#22c55e',
            fontSize: 13,
            marginBottom: 12
          }}>
            ✓ Bug report submitted successfully! We'll review it soon.
          </div>
        )}

        {submitStatus === 'error' && (
          <div style={{
            padding: '8px 12px',
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 4,
            color: '#ef4444',
            fontSize: 13,
            marginBottom: 12,
            lineHeight: 1.5
          }}>
            <div style={{ marginBottom: 4 }}>✗ Failed to submit bug report. Please try again or contact us directly:</div>
            <div style={{ fontSize: 12 }}>
              Email: <a href="mailto:support@precisiontraderapp.com" style={{ color: '#ef4444', textDecoration: 'underline' }}>support@precisiontraderapp.com</a>
              {' '}or visit our <a href="/contact" style={{ color: '#ef4444', textDecoration: 'underline' }}>Contact page</a>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting || !subject.trim() || !description.trim()}
          style={{
            width: '100%',
            padding: '10px',
            background: isSubmitting || !subject.trim() || !description.trim() ? colors.borderLight : colors.primary,
            color: colors.text,
            border: 'none',
            borderRadius: 6,
            fontWeight: 600,
            fontSize: 14,
            cursor: isSubmitting || !subject.trim() || !description.trim() ? 'not-allowed' : 'pointer',
            opacity: isSubmitting || !subject.trim() || !description.trim() ? 0.5 : 1
          }}
        >
          {isSubmitting ? 'Submitting...' : 'Submit Bug Report'}
        </button>
      </form>

      <div style={{ fontSize: 12, color: colors.textLight, marginBottom: 12, padding: '8px', background: colors.bgPrimary, borderRadius: 4 }}>
        ℹ️ Your current application state will be included with this bug report to help us diagnose the issue.
      </div>

      {/* State Preview */}
      <details style={{ borderTop: `1px solid ${colors.borderLight}`, paddingTop: 12 }}>
        <summary style={{ cursor: 'pointer', color: colors.primary, fontWeight: 600, marginBottom: 8 }}>
          View State Snapshot
        </summary>
        
        <div style={{ marginBottom: 12 }}>
          <button
            onClick={copyState}
            className="btn btn-sm"
            style={{ 
              color: colors.text, 
              border: `1px solid ${colors.borderLight}`, 
              padding: '4px 12px', 
              background: 'transparent',
              fontSize: '13px'
            }}
            title="Copy App State to Clipboard"
          >
            Copy State
          </button>
        </div>

        <Section title="User Settings" defaultOpen={true}>
          <JsonView value={{ user: auth.user }} />
        </Section>

        <Section title="Positions" defaultOpen={false}>
          <JsonView value={trading.positions || []} />
        </Section>

        <Section title="Orders" defaultOpen={false}>
          <JsonView value={{
            orders: filterRecentOrders(trading.orders),
            currentOrders: filterRecentOrders(streams.currentOrders),
            ordersMetadata: {
              totalOrders: (trading.orders || []).length,
              totalCurrentOrders: (streams.currentOrders || []).length,
              recentOrdersCount: filterRecentOrders(trading.orders).length,
              recentCurrentOrdersCount: filterRecentOrders(streams.currentOrders).length
            }
          }} />
        </Section>

        <Section title="Balances" defaultOpen={false}>
          <JsonView value={trading.balances || []} />
        </Section>

        <Section title="App State" defaultOpen={false}>
          <JsonView value={removeFunctionsDeep(app)} />
        </Section>

        <Section title="Market Aggregates" defaultOpen={false}>
          <JsonView value={streams.marketAggregates} />
        </Section>

        <Section title="Quotes" defaultOpen={false}>
          <JsonView value={{
            selectedTicker: trading.selectedTicker,
            selectedQuote: pickQuoteFields(
              streams.quotesBySymbol?.[trading.selectedTicker],
              { symbol: trading.selectedTicker }
            ),
            quotesCount: Object.keys(streams.quotesBySymbol || {}).length,
            allQuotes: allQuotes
          }} />
        </Section>

        <Section title="Bars" defaultOpen={false}>
          {activeChartBars ? (
            <JsonView value={{
              chartInfo: activeChartBars.chartInfo,
              rawBars: {
                totalBars: activeChartBars.chartInfo.totalBars,
                lastFiveBars: activeChartBars.bars.map((bar) => ({
                  TimeStamp: bar.TimeStamp,
                  Open: bar.Open,
                  High: bar.High,
                  Low: bar.Low,
                  Close: bar.Close,
                  TotalVolume: bar.TotalVolume,
                }))
              },
              chartData: {
                totalChartData: Array.isArray(chartData) ? chartData.length : 0,
                lastFiveChartData: lastFiveChartData ? lastFiveChartData.map(item => ({
                  time: item.time,
                  timeISO: new Date(item.time * 1000).toISOString(),
                  open: item.open,
                  high: item.high,
                  low: item.low,
                  close: item.close,
                  volume: item.volume
                })) : null
              }
            }} />
          ) : (
            <div style={{ color: colors.textLight, fontSize: 12, padding: '8px' }}>
              No bar data available for active chart
            </div>
          )}
        </Section>
      </details>
    </div>
  );
};

export default StateInspector;
